import React from "react";
import "./Styles.css";

const Deploy = () => (
  <div className="content">
    <h1>Deploy Your React App</h1>
    <p>
      This section will guide you through the process of deploying your React
      application to the Arweave permaweb using the ArDrive web app. Before we
      start, make sure you have an Arweave wallet with enough AR to cover the
      deployment costs.
    </p>

    <h2>Step 1: Build Your React App</h2>
    <p>
      Before you can deploy your React app, you need to build it. This creates a
      `build` folder in your project directory, which contains a version of your
      app that's optimized for production. Be sure to configure your
      package.json to default to relative filepaths first.
    </p>
    <p>
      Copy and paste this line into your package.json:
      <pre>"homepage": "./",</pre>
    </p>
    <p>
      To build your app, navigate to your project directory in your terminal and
      run the build script with the following:
    </p>
    <code>npm run build</code>
    <p>Or, if you're using Yarn:</p>
    <code>yarn build</code>

    <h2>Step 2: Log into ArDrive</h2>
    <p>
      Head over to the
      <a href="https://app.ardrive.io" target="_blank" rel="noreferrer">
        ArDrive web app
      </a>
      . Log in to the app as you normally would. If you haven't set up an
      account yet, you'll need to do that first.
    </p>

    <h2>Step 3: Select or Create a Drive</h2>
    <p>
      Once logged in, navigate to the drive where you want your project to be
      hosted. If you haven't created a drive yet, or if you want to make a new
      one specifically for this project, click the big red "New" button at the
      top left and create a new drive. Remember, the drive must be set to public
      for your project to be accessible to others.
    </p>

    <h2>Step 4: Upload Your Build Folder</h2>
    <p>
      With your drive selected, click the big red "New" button again, but this
      time, select "Upload Folder". Navigate to your project's build folder and
      select it. This will upload the entire folder, maintaining your project's
      file structure.
    </p>

    <h2>Step 5: Confirm the Upload</h2>
    <p>
      You'll be given a chance to review the upload and the associated cost. If
      everything looks right, click "Confirm". Remember, uploading to ArDrive
      isn't free, but the cost is usually quite small and the benefits of having
      your project on the permaweb are significant!
    </p>

    <h2>Step 6: Create a Manifest</h2>
    <p>
      After confirming the upload, navigate into your newly uploaded folder by
      double-clicking it. Click the big red "New" button once more and select
      "Create Manifest". You'll be prompted to name the manifest and choose
      where to save it. Be sure to save it inside the folder you just uploaded.
    </p>
    <h2>Step 7: Get the Data TX ID</h2>
    <p>
      Once the manifest is created, click on it to expand its details. On the
      bottom right, there's a line labeled "Data TX ID". This is the unique
      identifier for your uploaded project on ArDrive. Copy this value.
    </p>
    <h2>Step 8: View Your Project</h2>
    <p>
      Now it's time to see your project live on the permaweb! Append the Data TX
      ID you just copied to the end of an Arweave gateway URL, like
      `https://arweave.net/`. It might take a few minutes for your files to
      propagate through the network, but once they do, your site will be
      accessible to anyone, anywhere, at any time.
    </p>

    <h2>Done!</h2>
    <p>
      Congratulations, you've just deployed your React project to the permaweb
      using ArDrive! With your project now on the blockchain, it's stored
      permanently and can't be taken down or altered. You're officially a part
      of the decentralized web.
    </p>

    <h1>Updating</h1>
    <p>
      The dApp you uploaded will be available forever, it cannot be changed. You
      can still update your dApp by uploading a new build folder to the same
      location in the ArDrive web app, and then creating a new manifest for it.
      This will 'replace' the old files in any client that uses the ArFS
      protocol, like ArDrive. The old files will still be accessible by viewing
      file version history, and anyone with the old TX ID for the manifest will
      still be able to view the older version of the dApp, but creating the new
      manifest after uploading your updated build folder will create a new TX ID
      that can be used to view the updated dApp.
    </p>
  </div>
);

export default Deploy;
